import { api } from './axios';

import { getPartners } from './partners';
import { getPresentations } from './presentations';

import { EventEntity, EventsTimeline } from '../interfaces/events';
import { useEventStore } from '@/stores/events.store';
import { useContactStore } from '@/stores/contact.store';
import { ISO8601ToTime } from '@/helpers/isoToTime';

const getEventTime = (e: EventEntity) => {
  const date = Date.parse(e.plannedDateTime);
  const time = ISO8601ToTime(e.time)
    .split(':')
    .map((i) => Number(i));

  return date + (time[0] * 3600 + time[1] * 60) * 1000;
};

const sortByDate = (a: EventEntity, b: EventEntity) => {
  const firstDate = Date.parse(a.plannedDateTime);
  const secondDate = Date.parse(b.plannedDateTime);

  if (firstDate == secondDate) {
    return 0;
  } else if (firstDate > secondDate) {
    return -1;
  } else {
    return 1;
  }
};

const getEvents = async (): Promise<EventEntity[]> => {
  const eventsQuery = `query getEvents { 
    eventList(first: 50) { 
      nodes { 
        description 
        id 
        imageId 
        name 
        place 
        plannedDateTime 
        time
        shortDescription 
        mainGalleryImage
        signUpUrl 
        eventUrl
        isMainEvent
        tagsId
      } 
    } 
  }`;

  const events: Promise<EventEntity[]> = api
    .post('', {
      query: eventsQuery,
      operationName: 'getEvents'
    })
    .then((res: any) => {
      const events = res.data.data.eventList.nodes as EventEntity[];
      events.sort(sortByDate);
      return events;
    })
    .catch(async () => {
      setTimeout(() => {}, 1000);
      return await getEvents();
    });

  return events as Promise<EventEntity[]>;
};

const getTimeline = () => {
  const eventStore = useEventStore();
  const events = eventStore.getEvents.value as EventEntity[];

  const result: EventsTimeline = {
    prevEvents: [],
    nextEvents: []
  };

  const currentTime = new Date().getTime();

  events?.forEach((e: EventEntity) => {
    const eventTime = getEventTime(e) + 1000 * 3600 * 4;
    if (eventTime >= currentTime) {
      result.nextEvents.push(e);
    } else {
      result.prevEvents.push(e);
    }
  });

  result.nextEvents = result.nextEvents.sort(
    (x, y) => getEventTime(y) - getEventTime(x)
  );
  result.prevEvents = result.prevEvents.sort(
    (x, y) => getEventTime(y) - getEventTime(x)
  );

  return result;
};

const getFullEvent = async (id: string) => {
  const eventStore = useEventStore();
  const contactStore = useContactStore();

  const event = eventStore.getEventById(id);
  if (!event) return getFullEvent(id);

  const result = {
    event: event,
    presentations: await getPresentations(id),
    partners: await getPartners(id),
    contact: contactStore.getContact
  };

  return result;
};

const initializeEvents = () => {
  const eventStore = useEventStore();

  getEvents().then((res) => {
    eventStore.setEvents(res);
    eventStore.setEventsTimeline(getTimeline());
  });
};

export { initializeEvents, getEvents, getTimeline, getFullEvent, getEventTime };
